/**
 * Segnalazioni Page
 * Gestione segnalazioni problemi ospiti/staff
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, AlertTriangle, CheckCircle, Clock, Building2 } from "lucide-react";
import { useGetSegnalazioni } from "@/lib/supabaseQueries";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const STATO_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  "aperta": { label: "Aperta", variant: "destructive" },
  "in_corso": { label: "In corso", variant: "secondary" },
  "risolta": { label: "Risolta", variant: "default" },
};

export default function Segnalazioni() {
  const { data: segnalazioni = [], isLoading } = useGetSegnalazioni();
  const [filtroStato, setFiltroStato] = useState<string>("tutte");

  const filtrate = segnalazioni.filter((s: any) => {
    if (filtroStato === "tutte") return true;
    return s.stato === filtroStato;
  });

  const stats = {
    totali: segnalazioni.length,
    aperte: segnalazioni.filter((s: any) => s.stato === "aperta").length,
    inCorso: segnalazioni.filter((s: any) => s.stato === "in_corso").length,
    risolte: segnalazioni.filter((s: any) => s.stato === "risolta").length,
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Segnalazioni</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gestisci problemi e richieste da ospiti e staff
          </p>
        </div>
        <Link
          to="/operativo/segnalazioni/nuovo"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl font-medium text-sm hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nuova segnalazione
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Totali", value: stats.totali, icon: AlertTriangle, color: "text-foreground" },
          { label: "Aperte", value: stats.aperte, icon: AlertTriangle, color: "text-red-500" },
          { label: "In corso", value: stats.inCorso, icon: Clock, color: "text-yellow-500" },
          { label: "Risolte", value: stats.risolte, icon: CheckCircle, color: "text-green-500" },
        ].map((stat) => (
          <div key={stat.label} className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
              <span className="text-xs text-muted-foreground">{stat.label}</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: "tutte", label: "Tutte" },
          { key: "aperta", label: "Aperte" },
          { key: "in_corso", label: "In corso" },
          { key: "risolta", label: "Risolte" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFiltroStato(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filtroStato === f.key
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Lista */}
      {filtrate.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="text-lg font-medium text-foreground mb-2">Nessuna segnalazione</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {filtroStato === "tutte" 
              ? "Non ci sono segnalazioni. Clicca il pulsante in alto per crearne una."
              : "Nessuna segnalazione con questo stato."}
          </p>
          <Link
            to="/operativo/segnalazioni/nuovo"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nuova segnalazione
          </Link>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="divide-y divide-border">
            {filtrate.map((s: any) => (
              <Link
                key={s.id}
                to={`/operativo/segnalazioni/${s.id}`}
                className="flex items-start gap-4 p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex-shrink-0">
                  {s.stato === "risolta" ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : s.stato === "in_corso" ? (
                    <Clock className="h-5 w-5 text-yellow-500" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-medium text-foreground truncate">{s.titolo}</h3>
                    <Badge variant={STATO_BADGE[s.stato]?.variant || "default"}>
                      {STATO_BADGE[s.stato]?.label || s.stato}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{s.descrizione}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {s.struttura_nome || "Struttura non specificata"}
                    </span>
                    <span>•</span>
                    <span>{format(new Date(s.created_at), "dd MMM yyyy", { locale: it })}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
